import React from 'react';
import { Button } from '@opendate-app/setlist';
import formatPledgeAmount from '../../shared/utils/formatPledgeAmount';
import formatLongDate from '../../shared/utils/formatLongDate';

const fmt = (cents) => formatPledgeAmount(cents);

const STATUS_BADGES = {
  scheduled: { label: 'Scheduled', className: 'pd-b-gray' },
  overdue: { label: 'Overdue', className: 'pd-b-error' },
  processing: { label: 'Processing', className: 'pd-b-brand' },
  paid: { label: 'Paid', className: 'pd-b-success' },
  failed: { label: 'Payment failed', className: 'pd-b-error' },
  canceled: { label: 'Canceled', className: 'pd-b-gray' },
};

export default function InstallmentScheduleTable({ installments, onRetry, onResolve, retryingId }) {
  const total = installments.reduce((s, r) => s + Number(r.amount_cents || 0), 0);
  const paidTotal = installments
    .filter((r) => r.derived_status === 'paid')
    .reduce((s, r) => s + Number(r.charged_amount_cents || 0), 0);

  return (
    <table className="pd-itable">
      <thead>
        <tr>
          <th className="pd-itable-idx">#</th>
          <th>Due date</th>
          <th className="pd-itable-num">Amount</th>
          <th className="pd-itable-num">Paid</th>
          <th style={{ textAlign: 'right' }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {installments.map((row) => {
          // Preview installments (create mode) have no id yet; fall back to sequence.
          const rowKey = row.id ?? `seq-${row.sequence}`;
          const badge = STATUS_BADGES[row.derived_status] || STATUS_BADGES.scheduled;
          return (
            <tr key={rowKey}>
              <td className="pd-itable-idx">{row.sequence}</td>
              <td className="pd-itable-lead">{formatLongDate(row.due_date)}</td>
              <td className="pd-itable-num">{fmt(row.amount_cents)}</td>
              <td className="pd-itable-num">
                {row.derived_status === 'paid' ? fmt(row.charged_amount_cents) : <span className="pd-dash">—</span>}
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className={`pd-badge ${badge.className}`}>
                  <span className="pd-badge-dot" />{badge.label}
                </span>
                {row.derived_status === 'failed' && row.failure_reason && (
                  <div className="pd-itable-reason">{row.failure_reason}</div>
                )}
                {row.derived_status === 'failed' && (onRetry || onResolve) && (
                  <div className="pd-itable-retry">
                    {onRetry && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={retryingId != null}
                        aria-label={`Retry installment ${row.sequence}`}
                        onClick={() => onRetry(row)}
                      >
                        Retry
                      </Button>
                    )}
                    {onResolve && (
                      <Button
                        type="button"
                        variant="link-gray"
                        size="sm"
                        disabled={retryingId != null}
                        aria-label={`Mark installment ${row.sequence} resolved`}
                        onClick={() => onResolve(row)}
                      >
                        Mark resolved
                      </Button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td />
          <td>Total</td>
          <td className="pd-itable-num">{fmt(total)}</td>
          <td className="pd-itable-num">{fmt(paidTotal)}</td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}
